import { classNames } from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';
import { useCurrentUser } from 'src/manager/hooks/current-user';
import {
  findAccessCodePolicy,
  findEmailRegistrationPolicy,
  findEmailWhitelistPolicy,
  findSingleSignOnPolicy,
} from 'src/utils/graphql-helpers';

import { StateBadge } from '../../../components/state-badge';
import { State, useGetWebcastSecurityQuery, useGetWebcastStateQuery } from '../../../generated/graphql-manager';
import { useAbsoluteRoutes } from '../../../routes';

import styles from './live-state-info.module.css';

export function LiveStateInfo() {
  const { t } = useTranslation();
  const { lsproId } = useCurrentUser();
  const { webcastId = '' } = useParams();
  const { pathname } = useLocation();
  const routes = useAbsoluteRoutes();

  const stateResponse = useGetWebcastStateQuery({ variables: { webcastId }, skip: !webcastId });
  const securityResponse = useGetWebcastSecurityQuery({
    variables: { lsproId, webcastId },
    skip: !webcastId || !lsproId,
  });

  const state = stateResponse.data?.webcast?.state;

  if (state !== State.LIVE) return null;

  const isDetailsStep = pathname.includes(routes.webcastSetup_eventDetails);
  const isLanguageStep = pathname.includes(routes.webcastSetup_language);
  const isDescriptionStep = pathname.includes(routes.webcastSetup_eventDescription);
  const isMediaStep = pathname.includes(routes.webcastSetup_media);
  const isEventAccess = pathname.includes(routes.webcastSetup_eventAccess);
  const isEmbeddingStep = pathname.includes(routes.webcastSetup_embedding);
  const isMessagingStep = pathname.includes(routes.webcastSetup_messaging);
  const isRecordingStep = pathname.includes(routes.webcastSetup_eventRecording);
  const isProtocolStep = pathname.includes(routes.webcastSetup_protocolSettings);
  const isEcdnStep = pathname.includes(routes.webcastSetup_ecdn);

  const securityPolicies = securityResponse.data?.webcast.securityPolicies;
  const stateDisabled = !state || ![State.PRELIVE, State.LIVE].includes(state);
  const accessCodePolicy = findAccessCodePolicy(securityPolicies);
  const singleSignOnPolicy = findSingleSignOnPolicy(securityPolicies);
  const emailRegistrationPolicy = findEmailRegistrationPolicy(securityPolicies);
  const emailWhitelistPolicy = findEmailWhitelistPolicy(securityPolicies);
  const registrationEnabled = emailWhitelistPolicy ? true : emailRegistrationPolicy?.enabled;
  const publicEventChecked = [accessCodePolicy, singleSignOnPolicy, emailWhitelistPolicy].every(
    (policy) => !policy?.enabled
  );

  const eventAccessRestricted = stateDisabled || (publicEventChecked && state === State.LIVE);

  const changesNotPossible =
    isEmbeddingStep || isRecordingStep || isProtocolStep || isEcdnStep || (isEventAccess && eventAccessRestricted);

  const changesSavedAutomatically =
    isMediaStep || (isEventAccess && (!eventAccessRestricted || registrationEnabled)) || isMessagingStep;
  const changesSavedManually = isDetailsStep || isDescriptionStep;

  let infoBoxText = '';
  if (changesNotPossible) infoBoxText = t('manager.components.liveStateInfo.changesNotPossible');
  if (changesSavedAutomatically) infoBoxText = t('manager.components.liveStateInfo.changesSavedAutomatically');
  if (changesSavedManually) infoBoxText = t('manager.components.liveStateInfo.changesSavedManually');
  if (isLanguageStep) infoBoxText = t('manager.components.liveStateInfo.languageStep');

  return (
    <div
      data-testid="live-state-info"
      className={classNames(styles.wrapper, (changesSavedAutomatically || changesSavedManually) && styles.liveChanges)}
    >
      <StateBadge state={State.LIVE} />
      {infoBoxText}
    </div>
  );
}
